import { useState } from 'react';
import { toast } from 'react-toastify';
import { useForm } from 'react-hook-form';

import { createOrganizationSchema } from '../schemas';
import { yupResolver } from '@hookform/resolvers/yup';
import { omit } from 'lodash';
import { useCreateOrganizationMutation } from '@common/cache';
import { formatApiError } from '@common/errors/error-formatting.ts';
import { fillAddressFormValues } from '@util/google-places';

interface ICreateOrganizationFormShape {
  name: string;
  domains: string[];
  domainGenerator?: string;
  street: string;
  suite?: string;
  city: string;
  state: string;
  zip_code: string;
  county?: string;
  tin?: string;
  guide_participant_id?: string;
  google_places_id: string;
}

const initialValues: ICreateOrganizationFormShape = {
  name: '',
  domains: [],
  domainGenerator: '',
  google_places_id: '',
  street: '',
  suite: '',
  city: '',
  state: '',
  zip_code: '',
  county: '',
  tin: '',
  guide_participant_id: '',
};

export const useCreateOrganization = (
  handleOpenOrganizationDetails: (id: string) => void,
) => {
  const formMethods = useForm<ICreateOrganizationFormShape>({
    mode: 'onChange',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: yupResolver<ICreateOrganizationFormShape, any, any>(
      createOrganizationSchema,
    ),
    defaultValues: initialValues,
  });
  const {
    handleSubmit,
    setValue,
    getValues,
    trigger,
    reset,
    formState: { isSubmitting, isDirty, isValid },
  } = formMethods;
  const { mutateAsync, isPending } = useCreateOrganizationMutation();
  const [isOpen, setIsOpen] = useState(false);
  const [isSaveChangesModalOpen, setIsSaveChangesModalOpen] = useState(false);

  const handleClose = () => {
    if (isDirty) {
      return setIsSaveChangesModalOpen(true);
    }
    setIsOpen(false);
    reset();
  };

  const handleOnClick = () => {
    setIsOpen(true);
  };

  const handleAddDomain = (newDomain?: string) => {
    const domains = getValues('domains');
    if (newDomain) setValue('domains', [...domains, newDomain]);
    trigger('domainGenerator');
  };

  const handleRemoveDomain = (index: number) => {
    const domains = getValues('domains');
    domains.splice(index, 1);
    setValue('domains', domains);
    trigger('domainGenerator');
  };

  const handleSelectLocation = (place: Partial<google.maps.places.Place>) => {
    fillAddressFormValues({ setValue, place });
  };

  const submitHandler = async (values: ICreateOrganizationFormShape) => {
    try {
      const response = await mutateAsync({
        ...omit(values, ['domainGenerator']),
        country: 'US',
      });

      if (response.data.id) {
        handleOpenOrganizationDetails(response.data.id);
      }

      toast.success('You added a new organization');
      setIsOpen(false);
      reset();
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const onSubmit = handleSubmit(submitHandler);

  const handleCloseSaveChangesModal = () => {
    setIsSaveChangesModalOpen(false);
  };

  const handleDiscardChanges = () => {
    setIsSaveChangesModalOpen(false);
    setIsOpen(false);
    reset();
  };

  const handleSaveChanges = async () => {
    const values = getValues();

    try {
      await mutateAsync({
        ...omit(values, [
          'domainGenerator',
          'tin',
          'guide_participant_id',
          'suite',
        ]),
        country: 'US',
        ...(values.tin && { tin: values.tin }),
        ...(values.guide_participant_id && {
          guide_participant_id: values.guide_participant_id,
        }),
        ...(values.suite && { suite: values.suite }),
      });

      toast.success('You added a new organization');
      setIsSaveChangesModalOpen(false);
      setIsOpen(false);
      reset();
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  return {
    formMethods,
    onSubmit,
    isSubmitting,
    isOpen,
    submitHandler,
    handleSubmit,
    handleClose,
    handleOnClick,
    handleAddDomain,
    handleRemoveDomain,
    handleSelectLocation,
    isSaveChangesModalOpen,
    handleCloseSaveChangesModal,
    handleSaveChanges,
    handleDiscardChanges,
    isValid,
    isEditSubmitting: isPending,
  };
};
